/**
 * The downloadable assets a guide can offer in exchange for a contact detail.
 *
 * ── Why these exist ──────────────────────────────────────────────────────────
 * The guides rank for problems, not for products: *RA bill format*, *labour
 * attendance register format*, *cent to square feet*. Somebody searching those
 * has a bill to raise or a register to keep this week and has not decided to buy
 * software — so the matching ask is **the thing they searched for**, not a demo.
 * A reader who downloads a blank RA bill format is a builder who raises RA
 * bills, which is a better-qualified lead than most demo requests.
 *
 * ── Why they are real files ─────────────────────────────────────────────────
 * The earlier audit said a download link that produces a placeholder is worse
 * than no download link, and that stands. Each file below is a working sheet. The first three:
 * the RA bill format carries the nine columns the guide itself explains plus the
 * deduction block and the sign-off rows; the muster is a real 31-day grid with
 * the marking legend; the conversion sheet is the guide's own table plus the
 * multipliers and a worked example. The four added later follow the same rule: the work order carries every clause
 * its guide argues for, the indent/PO pair carries the ordered-received-billed
 * comparison the guide turns on, the expense register carries the imprest
 * reconciliation block, and the labour calculator builds the loaded day rate
 * component by component. All are derived from the guide text, so the page and
 * the file cannot disagree.
 *
 * No spreadsheet formulas, deliberately. A formula referencing a cell the
 * reader inserted a row above is a broken file arriving with our name on it,
 * and a sheet that computes nothing is easier to trust than one that computes
 * something wrong. Totals are marked where they belong.
 *
 * CSV rather than XLSX: it opens in Excel, Google Sheets, Numbers and LibreOffice
 * with no library on our side and no corrupted-file support mail. The cost is no
 * formatting, which for a blank register nobody needs.
 *
 * Where statute is involved, the file says so in its own footer rather than
 * quoting a rate — the same line the guides hold.
 *
 * ── Keep in step with the API ───────────────────────────────────────────────
 * `Builder-API/utils/leadAssets.js` holds the same slug → file/label map,
 * because the confirmation email has to link the file and must NOT take a URL
 * from the browser (a client-supplied link in an email we send is a phishing
 * vector). Two small maps, one on each side; the API ignores any slug it does
 * not know.
 */

export interface LeadAsset {
  /** URL slug, used in `?asset=` and as the key on both sides. */
  slug: string;
  /** Public path of the file. */
  file: string;
  /** What it is, in the words a reader would use. */
  label: string;
  /** The button text on the guide. */
  cta: string;
}

export const LEAD_ASSETS: LeadAsset[] = [
  {
    slug: 'ra-bill-format',
    file: '/downloads/ra-bill-format.csv',
    label: 'blank RA bill format',
    cta: 'Get the blank RA bill format',
  },
  {
    slug: 'labour-muster-register',
    file: '/downloads/labour-muster-register.csv',
    label: 'monthly muster register',
    cta: 'Get the muster register sheet',
  },
  {
    slug: 'tamil-nadu-land-unit-conversion',
    file: '/downloads/tamil-nadu-land-unit-conversion.csv',
    label: 'cent / ground conversion sheet',
    cta: 'Get the conversion sheet',
  },
  {
    slug: 'work-order-format',
    file: '/downloads/work-order-format.csv',
    label: 'blank work order format',
    cta: 'Get the blank work order format',
  },
  {
    slug: 'material-indent-po-format',
    file: '/downloads/material-indent-po-format.csv',
    label: 'site indent and purchase order formats',
    cta: 'Get the indent and PO formats',
  },
  {
    slug: 'site-expense-register',
    file: '/downloads/site-expense-register.csv',
    label: 'site expense register',
    cta: 'Get the site expense register',
  },
  {
    slug: 'labour-cost-calculator',
    file: '/downloads/labour-cost-calculator.csv',
    label: 'labour cost calculator',
    cta: 'Get the labour cost calculator',
  },
];

export const leadAssetBySlug = (slug?: string | null): LeadAsset | undefined =>
  (slug ? LEAD_ASSETS.find((a) => a.slug === slug) : undefined);
